import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector, batch } from "react-redux";
import {
  Container,
  LeftSection,
  Loader,
  PlayerLeftFrame,
  Preview,
  PreviewContainer,
  RightSection,
} from "./shorts-components";
import WorkspaceReframeLoader from "./workspace-reframe-loader";
import Workspace from "../workspace/workspace";
import Spinner, { SPINNER_TYPE } from "../../common-components/Spinner";
import { setAnimoPlayerId, setShortContainerId, setShortsPlayer, setPlayAll, getPresetItems, playPausePlayer, setReframeData } from "../../redux/actions/appUtils";
import ActionTypes from "../../constants/action-types";
import { getSubtitlesByDuration, getNewProject, getShortsPlayerDimensions, getShortsPlayerConstraints } from "./shorts-helper";
import ShortsPlayerControlPanel from "./shorts-player-control";
import ShortsContent from "./shorts-content";
import { getWorkspaceItemSource } from "../../helper/URLHelper";
import { useShortsContext } from "./shortsContextApi";
import contextActionType from "./context-action-type";
import ShortsCardWatermark from "./shorts-card-watermark";
// import { PLAN } from "../../constants";

const { GET_ACTIVE_PLAYER, SET_ACTIVE_PLAYER, GET_REFRAME_LOADING, GET_REFRAME_PROGRESS, GET_PRESET_MODAL_OPEN } = contextActionType;

const ShortsPlayer = ({
  projectDetails,
  userDetails,
  shortData,
  appReframeData,
  inModal = false,
  showPricingModal,
  feedbackType,
  onLike,
  onDislike,
  parentDispatch,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isShortsPage = location.pathname.includes("shorts");
  const appReducer = useSelector((state) => state.app);
  const t1 = useSelector((state) => state.app.get("t1"));
  const initStages = appReducer.get("initStages");
  const animoPlayerId = useMemo(() => `animo-player-${shortData?.get("id")}`, [shortData]);
  const shortContainerId = useMemo(() => `shorts-preview-${shortData?.get("id")}`, [shortData]);
  const videoElRef = useRef();
  // Tracks the last short ID for which SET_PROJECT was dispatched.
  // Prevents non-reframe shorts from re-dispatching on every appReframeData
  // change (which fires each time another short's reframe JSON finishes loading).
  const lastDispatchedShortIdRef = useRef(null);
  // Tracks the localSubtitle / workspaceItems references the player was last
  // built with. When a subtitle style preset is applied (or workspace items
  // change) for the SAME short, these references change but the short id does
  // not — so the effect must rebuild instead of hitting the non-reframe
  // early-return guard below.
  const lastLocalSubtitleRef = useRef(null);
  const lastWorkspaceItemsRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const wasReframeRef = useRef(false);
  // Tracks whether the player has already been (re)built WITH the reframe scene
  // data for the current short. A reframe short can mount before its scene JSON
  // has been fetched into the store, so on mount we must wait for the data to
  // land and then force a clean reframe rebuild — even though isReframe was
  // already true at mount time. Reset whenever the short changes.
  const reframeReadyBuiltRef = useRef(false);
  const { getShortsContext, setShortsContext } = useShortsContext();
  const activePlayerId = getShortsContext(GET_ACTIVE_PLAYER);
  const isReframeApplying = getShortsContext(GET_REFRAME_LOADING);
  const reframeProgress = getShortsContext(GET_REFRAME_PROGRESS) || 0;
  const presetModalOpen = getShortsContext(GET_PRESET_MODAL_OPEN);
   const prevPresetModalOpenRef = useRef(false);
  const [subtitlesInRange, setSubtitlesInRange] = useState([]);
  const [videoSrc, setVideoSrc] = useState("");
  const [timelineDuration, setTimelineDuration] = useState(0);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [playbackVersion, setPlaybackVersion] = useState(0);
  const lastVideoSrcRef = useRef("");
  const { dimensionName } = useSelector((state) => state.userDetails);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  }));

  useEffect(() => {
    if (!isShortsPage) {
      return undefined;
    }

    const onResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [isShortsPage]);

  const playerConstraints = useMemo(() => {
    if (!isShortsPage) {
      return null;
    }
    return getShortsPlayerConstraints(viewport.width, viewport.height, dimensionName);
  }, [isShortsPage, viewport, dimensionName]);

  useEffect(() => {
    if (activePlayerId && activePlayerId !== animoPlayerId) {
      // Pause this player if another player is active
      dispatch(setPlayAll(false, false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlayerId, animoPlayerId]);

  const pausePlayer = useCallback(() => {
    const start = shortData.get("start");
    let playhead = start;
    if (t1 && typeof t1.time === "function") {
      playhead = t1.time();
    }
    dispatch(playPausePlayer({ playhead, isPause: true }));
    dispatch(setPlayAll(false, false));
    setShortsContext(SET_ACTIVE_PLAYER, null);
  }, [dispatch, shortData, t1, setShortsContext]);


  const getTime = useCallback((short) => {
    const currentShort = short.toJS();
    const { start, end, clips } = currentShort;
    if (clips && clips.length > 0) {
      const startTime = clips[0].startTime || start;
      const endTime = clips[clips.length - 1].endTime || end;
      return {
        start: startTime,
        end: endTime
      };
    }
    return {
      start,
      end
    };
  }, []);
  const { start, end } = getTime(shortData);
  const isAireframeShort = useMemo(() => {
    const shortDataJS = shortData.toJS ? shortData.toJS() : shortData;
    return (shortDataJS.isReframe && !!shortDataJS.reframeSrc)
      || shortDataJS.clips?.some((clip) => clip.isReframe && !!clip.reframeSrc);
  }, [shortData]);

  useEffect(() => {
    if (location.pathname.includes('shorts')) {
      dispatch(getPresetItems());
    }
  }, [dispatch, location]);

  useEffect(() => {
    if (userDetails) {
      const shortDataJS = shortData.toJS();
      const reframeDataJS = appReframeData
        ? (appReframeData.toJS ? appReframeData.toJS() : appReframeData)
        : null;

      // Detect the subtitle-style preset modal closing (open -> closed). The ref
      // is updated on every effect run so transitions are tracked even when the
      // effect early-returns below.
      const presetJustClosed = prevPresetModalOpenRef.current && !presetModalOpen;
      prevPresetModalOpenRef.current = presetModalOpen;

      // A short is only considered "reframe-ready" when it has both isReframe: true AND
      // a reframeSrc URL. The optimistic update (applied while the API is in-flight) only
      // sets isReframe: true with no reframeSrc — that must NOT trigger a player rebuild/autoplay.
      const isReframeShort = (shortDataJS.isReframe && !!shortDataJS.reframeSrc)
        || shortDataJS.clips?.some(c => c.isReframe && !!c.reframeSrc);

      // Resolve the base asset id from the project's video item so we can tell
      // whether the reframe scene JSON for THIS short has actually arrived in the
      // store yet (it is fetched asynchronously by shorts-content).
      const wsItemsForAsset = projectDetails.get("workspaceItems");
      const videoItemForAsset = wsItemsForAsset?.valueSeq
        ? wsItemsForAsset.valueSeq().find((it) => (it.get ? it.get("type") : it.type) === "UPVIDEO")
        : null;
      const baseAssetId = videoItemForAsset
        ? (videoItemForAsset.get ? videoItemForAsset.get("assetId") : videoItemForAsset.assetId)
        : null;

      // The reframe video can only be prepared once the scene JSON for this short
      // is present in the store. It may be keyed by the composite
      // `${assetId}_${shortId}` or by the plain assetId.
      const hasReframeSceneData = (() => {
        if (!isReframeShort || !reframeDataJS || !baseAssetId) {
          return false;
        }
        const compositeKey = `${baseAssetId}_${shortDataJS.id}`;
        const entry = reframeDataJS[compositeKey]
          || reframeDataJS[baseAssetId]
          || (reframeDataJS.assetId === baseAssetId ? reframeDataJS : null);
        return !!(entry && entry.scenes && entry.scenes.length);
      })();

      if (lastDispatchedShortIdRef.current !== shortDataJS.id) {
        wasReframeRef.current = isReframeShort;
        reframeReadyBuiltRef.current = false;
      }

      const didReframeApply = isReframeShort && lastDispatchedShortIdRef.current === shortDataJS.id && !wasReframeRef.current;

      // A reframe short can mount (or be reopened) BEFORE its scene JSON has been
      // fetched. When the data lands, rebuild the player with the reframe applied
      // — even though isReframe was already true at mount, so didReframeApply is
      // false. This guarantees the player always prepares the reframe video once
      // the reframe signal is true and its data is available.
      const didReframeBecomeReady = isReframeShort && hasReframeSceneData && !reframeReadyBuiltRef.current;

      const currentLocalSubtitle = projectDetails.get("localSubtitle");
      const currentWorkspaceItems = projectDetails.get("workspaceItems");
      const projectContentChanged =
        lastDispatchedShortIdRef.current === shortDataJS.id &&
        (lastLocalSubtitleRef.current !== currentLocalSubtitle ||
          lastWorkspaceItemsRef.current !== currentWorkspaceItems);

      // For non-reframe shorts: only run the full project setup once per short.
      // When appReframeData changes (other shorts' reframe JSONs loading), this
      // effect re-fires for ALL players — but normal shorts don't need to
      // re-dispatch SET_PROJECT each time. Subtitle/workspace edits are the
      // exception and must still rebuild.
      if (!didReframeApply && !isReframeShort && !projectContentChanged && !presetJustClosed && lastDispatchedShortIdRef.current === shortDataJS.id) {
        return;
      }

      if (didReframeApply) {
        wasReframeRef.current = true;
      }
      // Once we build with the reframe scene data present, remember it so we don't
      // keep forcing teardown/rebuilds on every subsequent appReframeData change.
      if (hasReframeSceneData) {
        reframeReadyBuiltRef.current = true;
      }

      const newProject = getNewProject(projectDetails.toJS(), shortDataJS, reframeDataJS);

      setTimelineDuration(newProject.duration);
      const subtitles = getSubtitlesByDuration(
        start,
        end,
        projectDetails.get("localSubtitle").toJS()
      );
      setSubtitlesInRange(subtitles);
      const keys = Object.keys(newProject.workspaceItems);
      const itemKey = keys.find(
        (key) => newProject.workspaceItems[key].type === "UPVIDEO" && !newProject.workspaceItems[key].sceneId
      ) || keys[0];
      const item = newProject.workspaceItems[itemKey];
      const { src } = getWorkspaceItemSource({
        item: { src: item.src, subType: item.subType, type: item.type },
      });
      const videoElement = videoElRef.current;
      const isSameVideoSrc = lastVideoSrcRef.current === src;
      lastVideoSrcRef.current = src;
      setVideoSrc(src);
      setIsVideoReady(isSameVideoSrc && videoElement?.readyState >= 1);

      batch(() => {
        lastDispatchedShortIdRef.current = shortDataJS.id;
        // Remember the content references we just built with so subsequent
        // subtitle/workspace edits for this same short are detected as changes.
        lastLocalSubtitleRef.current = currentLocalSubtitle;
        lastWorkspaceItemsRef.current = currentWorkspaceItems;
        if (didReframeApply || didReframeBecomeReady) {
          // Tear down the existing timeline so it is rebuilt cleanly from the
          // reframe-applied project (either freshly applied in-session, or now
          // that the reframe scene data has become available on mount/reopen).
          dispatch(setPlayAll(false, false));
        }
        dispatch(setAnimoPlayerId(animoPlayerId));
        dispatch(setShortsPlayer({ isShortsPlayer: true }));
        dispatch(setShortContainerId(shortContainerId));
        dispatch({ type: ActionTypes.SET_USER_DATA, payload: { ...userDetails } });
        dispatch({ type: ActionTypes.SET_PROJECT, payload: newProject });
        // Force the animo-player timeline to rebuild with the newly set project items
        dispatch({ type: ActionTypes.UPDATE_PROJECT_DATA_COMPLETE, payload: { toUpdate: {} } });
        
        if (appReframeData) {
          // The global store uses composite keys (assetId_shortId) to avoid collisions.
          // The local player GSAP engine expects plain assetId keys. Re-key for the current short.
          const localReframeData = reframeDataJS ? { ...reframeDataJS } : {};
          if (shortDataJS.id && baseAssetId) {
            const compositeKey = `${baseAssetId}_${shortDataJS.id}`;
            if (localReframeData[compositeKey]) {
              localReframeData[baseAssetId] = localReframeData[compositeKey];
            }
          }
          dispatch(setReframeData(localReframeData));
        }

        if (src) {
          // Kick off the GSAP timeline build. SET_PLAY_ALL(true) creates a new t1
          // timeline in Redux. The ShortsPlayerControlPanel watches for t1 to
          // become available and fires the actual playPausePlayer dispatch then.
          dispatch(setPlayAll(true, false));
        } 
      });

      if (src && videoElement) {
      
        videoElement.pause();
        videoElement.currentTime = start || 0;
        videoElement.style.display = "none";
      }
      setPlaybackVersion((version) => version + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails, userDetails, shortData, appReframeData, start, presetModalOpen]);

  const clippedProject = useMemo(() => {
    const reframeDataJS = appReframeData
      ? (appReframeData.toJS ? appReframeData.toJS() : appReframeData)
      : null;
    return getNewProject(projectDetails.toJS(), shortData.toJS(), reframeDataJS);
  }, [projectDetails, shortData, appReframeData]);

  let workspaceComponent = null;
  if (initStages.get("projectLoaded")) {
    workspaceComponent = <Workspace />;
  }

  const timeUpdateHandler = (e) => {
    const { currentTime } = e.target;
    if (currentTime >= end) {
      videoElRef.current.pause();
      videoElRef.current.currentTime = start;
      videoElRef.current.style.display = "none";
    }
  };

  const { videoStyles, containerStyles } = useMemo(() => {
    const { width: projectWidth, height: projectHeight, workspaceItems } = clippedProject;
    const itemKey = Object.keys(workspaceItems).find(
      (key) => workspaceItems[key].type === "UPVIDEO" && !workspaceItems[key].sceneId
    ) || Object.keys(workspaceItems)[0];
    const clipItem = itemKey ? workspaceItems[itemKey] : null;

    if (!clipItem) {
      return { videoStyles: {}, containerStyles: {} };
    }

    const { effectiveZoom, containerWidth, containerHeight } = getShortsPlayerDimensions(
      dimensionName,
      projectWidth,
      projectHeight,
      playerConstraints
    );

    return {
      videoStyles: {
        width: `${Math.round(clipItem.width * effectiveZoom)}px`,
        height: `${Math.round(clipItem.height * effectiveZoom)}px`,
        top: `${Math.round(clipItem.y * effectiveZoom)}px`,
        left: `${Math.round(clipItem.x * effectiveZoom)}px`,
      },
      containerStyles: {
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
      },
    };
  }, [clippedProject, dimensionName, playerConstraints]);

  const showPlayerWatermark = false;

  const handleWatermarkClick = useCallback((e) => {
    e.stopPropagation();
    pausePlayer();
    showPricingModal?.({ type: "longtoshort", source: "shorts-player-watermark" });
  }, [pausePlayer, showPricingModal]);

  return (
    <Container isVertical={dimensionName === "vertical"} $inModal={inModal}>
      <PlayerLeftFrame style={{ width: containerStyles.width, maxWidth: "100%" }}>
        <LeftSection
          id={animoPlayerId}
          style={containerStyles}
          $hasWatermarkBar={showPlayerWatermark}
        >
          {workspaceComponent || (
            <Loader>
              <Spinner type={SPINNER_TYPE.RING_SPINNER} spinnerHeight={"130px"} />
            </Loader>
          )}
          <PreviewContainer id={shortContainerId}>
            <Preview
              ref={videoElRef}
              $height={videoStyles.height}
              $width={videoStyles.width}
              $left={videoStyles.left}
              $top={videoStyles.top}
              src={videoSrc}
              preload="metadata"
              muted
              onLoadedMetadata={(e) => {
                e.target.currentTime = start || 0;
                setIsVideoReady(true);
              }}
              onTimeUpdate={timeUpdateHandler}
            />
          </PreviewContainer>
          <ShortsPlayerControlPanel
            startTime={0}
            endTime={timelineDuration}
            videoStartTime={start}
            isAireframeShort={isAireframeShort}
            playbackVersion={playbackVersion}
            videoRef={videoElRef}
            isShorts={isShortsPage}
            isVideoReady={isVideoReady}
          />
          {isReframeApplying && (
            <WorkspaceReframeLoader
              progress={reframeProgress}
              duration={timelineDuration || (end - start)}
            />
          )}
        </LeftSection>
        {showPlayerWatermark && (
          <ShortsCardWatermark onClick={handleWatermarkClick} isVertical={dimensionName === "vertical"} />
        )}
      </PlayerLeftFrame>
      <RightSection $dimensionName={dimensionName} $inModal={inModal}>
        <ShortsContent
          shortData={shortData}
          subtitlesInRange={subtitlesInRange}
          inModal={inModal}
          showPricingModal={showPricingModal}
          feedbackType={feedbackType}
          onLike={onLike}
          onDislike={onDislike}
          parentDispatch={parentDispatch}
        />
      </RightSection>
    </Container>
  );
};

ShortsPlayer.propTypes = {
  projectDetails: PropTypes.object,
  userDetails: PropTypes.object,
  shortData: PropTypes.object,
  appReframeData: PropTypes.object,
  inModal: PropTypes.bool,
  showPricingModal: PropTypes.func,
  feedbackType: PropTypes.oneOf(["like", "dislike", null]),
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
  parentDispatch: PropTypes.func,
};

export default React.memo(ShortsPlayer, (prevProps, nextProps) => {
  return (
    prevProps.shortData === nextProps.shortData
    && prevProps.userDetails === nextProps.userDetails
    && prevProps.appReframeData === nextProps.appReframeData
    && prevProps.projectDetails.get("workspaceItems") === nextProps.projectDetails.get("workspaceItems")
    && prevProps.projectDetails.get("localSubtitle") === nextProps.projectDetails.get("localSubtitle")
    && prevProps.feedbackType === nextProps.feedbackType
  );
});
